/**
 * Component Preloader System
 * 
 * This system preloads components to reduce navigation loading times
 * and provides a smoother user experience when switching between tabs.
 * 
 * IMPORTANT: This only affects JavaScript module loading, NOT REST API calls.
 * Your existing API caching systems (React Query, RequestDeduplicator)
 * continue to handle all data fetching and caching independently.
 * 
 * What this preloader does:
 * - Caches JavaScript modules in memory for instant loading
 * - Prevents duplicate module imports
 * - Provides faster component instantiation
 * 
 * What this preloader does NOT do:
 * - Cache API responses (handled by your existing systems)
 * - Affect data fetching (handled by your existing systems)
 * - Modify component behavior (components work exactly the same)
 */

import React from 'react'

class ComponentPreloader {
  constructor() {
    this.preloadedComponents = new Map()
    this.preloadQueue = new Set()
    this.isPreloading = false
  }

  /**
   * Preload a component by importing it
   * @param {string} componentKey - Unique key for the component
   * @param {Function} importFunction - Function that returns a promise resolving to the component
   */
  async preloadComponent(componentKey, importFunction) {
    // Skip if already preloaded or currently preloading
    if (this.preloadedComponents.has(componentKey) || this.preloadQueue.has(componentKey)) {
      return this.preloadedComponents.get(componentKey)
    }

    this.preloadQueue.add(componentKey)

    try {
      const component = await importFunction()
      this.preloadedComponents.set(componentKey, component)
      this.preloadQueue.delete(componentKey)
      return component
    } catch (error) {
      console.warn(`Failed to preload component ${componentKey}:`, error)
      this.preloadQueue.delete(componentKey)
      throw error
    }
  }

  /**
   * Get a preloaded component or return null if not preloaded
   * @param {string} componentKey - Unique key for the component
   */
  getPreloadedComponent(componentKey) {
    return this.preloadedComponents.get(componentKey) || null
  }

  /**
   * Check if a component is preloaded
   * @param {string} componentKey - Unique key for the component
   */
  isComponentPreloaded(componentKey) {
    return this.preloadedComponents.has(componentKey)
  }

  /**
   * Preload multiple components in parallel
   * @param {Array} components - Array of {key, importFunction} objects
   */
  async preloadComponents(components) {
    const preloadPromises = components.map(({ key, importFunction }) =>
      this.preloadComponent(key, importFunction).catch(error => {
        console.warn(`Failed to preload ${key}:`, error)
        return null
      })
    )

    await Promise.allSettled(preloadPromises)
  }

  /**
   * Clear preloaded components (useful for memory management)
   * @param {string} pattern - Optional pattern to match component keys
   */
  clearPreloadedComponents(pattern = null) {
    if (pattern) {
      const regex = new RegExp(pattern)
      for (const key of this.preloadedComponents.keys()) {
        if (regex.test(key)) {
          this.preloadedComponents.delete(key)
        }
      }
    } else {
      this.preloadedComponents.clear()
    }
  }

  /**
   * Get preload statistics
   */
  getStats() {
    return {
      preloadedCount: this.preloadedComponents.size,
      preloadingCount: this.preloadQueue.size,
      preloadedKeys: Array.from(this.preloadedComponents.keys()),
      preloadingKeys: Array.from(this.preloadQueue)
    }
  }
}

// Create singleton instance
const componentPreloader = new ComponentPreloader()

/**
 * Retry loading a chunk with cache-busting
 * Handles 404 errors from stale browser/SW cache after deployments
 */
const loadWithRetry = async (importFunction, componentKey, maxRetries = 2) => {
  let lastError = null
  let detected404 = false

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const component = await importFunction()
      return component
    } catch (error) {
      lastError = error
      const is404 = error.message?.includes('404') ||
                    error.message?.includes('Failed to fetch') ||
                    error.message?.includes('dynamically imported module')

      if (is404) {
        detected404 = true

        if (attempt < maxRetries) {
          console.warn(`[componentPreloader] Chunk load failed for ${componentKey}, attempt ${attempt + 1}/${maxRetries + 1}. Clearing cache and retrying...`)

          // Clear service worker caches to force fresh fetch
          if ('caches' in window) {
            try {
              const cacheNames = await caches.keys()
              await Promise.all(
                cacheNames.map(name => caches.delete(name))
              )
              console.log('[componentPreloader] Cleared browser caches')
            } catch (cacheError) {
              console.warn('[componentPreloader] Failed to clear caches:', cacheError)
            }
          }

          // Small delay before retry
          await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)))
          continue
        }
      }

      // Non-404 error or max retries reached
      break
    }
  }

  // All retries failed
  console.error(`[componentPreloader] Failed to load ${componentKey} after ${maxRetries + 1} attempts. A page refresh may be needed.`)

  // If we detected a 404, dispatch the stale deployment event to show reload prompt
  if (detected404) {
    console.warn('[componentPreloader] Dispatching stale deployment event for:', componentKey)
    window.dispatchEvent(new CustomEvent('sw-stale-deployment', {
      detail: {
        failedUrl: componentKey,
        message: 'A new version has been deployed. Please refresh to load the latest version.'
      }
    }))
  }

  throw lastError
}

/**
 * Enhanced lazy component that uses preloading with retry logic
 * @param {Function} importFunction - Function that returns a promise resolving to the component
 * @param {string} componentKey - Unique key for the component
 */
export const createPreloadableLazyComponent = (importFunction, componentKey) => {
  return React.lazy(async () => {
    // Try to get preloaded component first
    const preloaded = componentPreloader.getPreloadedComponent(componentKey)
    if (preloaded) {
      return preloaded
    }

    // If not preloaded, load it now with retry logic
    try {
      const component = await loadWithRetry(importFunction, componentKey)
      componentPreloader.preloadedComponents.set(componentKey, component)
      return component
    } catch (error) {
      console.error(`Failed to load component ${componentKey}:`, error)
      throw error
    }
  })
}

/**
 * Preload components for common routes
 */
export const preloadCommonComponents = async () => {
  const commonComponents = [
    {
      key: 'dashboard',
      importFunction: () => import('../features/dashboard/components/DailyDashboard')
    },
    {
      key: 'clients',
      importFunction: () => import('../features/clients/Clients')
    },
    {
      key: 'jobs',
      importFunction: () => import('../features/jobs/JobsList')
    },
    {
      key: 'estimates',
      importFunction: () => import('../features/estimates/Estimates')
    },
    {
      key: 'invoices',
      importFunction: () => import('../features/invoices/Invoices')
    },
    {
      key: 'expenses',
      importFunction: () => import('../features/expenses/Expenses')
    },
    {
      key: 'employees',
      importFunction: () => import('../features/employees/Employees')
    },
    {
      key: 'settings',
      importFunction: () => import('../features/settings/Settings')
    }
  ]

  await componentPreloader.preloadComponents(commonComponents)
}

/**
 * Preload components on user interaction (hover, focus)
 */
export const preloadOnInteraction = (componentKey, importFunction) => {
  return componentPreloader.preloadComponent(componentKey, importFunction)
}

export default componentPreloader